/**
 * AgentStatusBadge Component
 * Displays real-time agent status with color-coded badge (dark theme)
 */

import React from 'react';

const AgentStatusBadge = ({ status, lastHeartbeat, size = 'md' }) => {
  const getStatusInfo = () => {
    switch (status) {
      case 'online':
        return {
          dotColor: 'bg-brand-green',
          textColor: 'text-brand-green',
          bgColor: 'bg-brand-green/10',
          borderColor: 'border-brand-green/30',
          label: 'En ligne',
          pulse: true
        };
      case 'offline':
        return {
          dotColor: 'bg-gray-500',
          textColor: 'text-gray-400',
          bgColor: 'bg-gray-500/10',
          borderColor: 'border-gray-500/30',
          label: 'Hors ligne',
          pulse: false
        };
      case 'busy':
        return {
          dotColor: 'bg-orange-500',
          textColor: 'text-orange-400',
          bgColor: 'bg-orange-500/10',
          borderColor: 'border-orange-500/30',
          label: 'Busy',
          pulse: true
        };
      case 'error':
        return {
          dotColor: 'bg-red-500',
          textColor: 'text-red-400',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/30',
          label: 'Erreur',
          pulse: false
        };
      case 'disabled':
        return {
          dotColor: 'bg-gray-600',
          textColor: 'text-gray-500',
          bgColor: 'bg-gray-600/10',
          borderColor: 'border-gray-600/30',
          label: 'Disabled',
          pulse: false
        };
      default:
        return {
          dotColor: 'bg-gray-600',
          textColor: 'text-gray-500',
          bgColor: 'bg-gray-600/10',
          borderColor: 'border-gray-600/30',
          label: 'Inconnu',
          pulse: false
        };
    }
  };

  const getTimeSinceHeartbeat = () => {
    if (!lastHeartbeat) return null;
    const now = new Date();
    const heartbeatDate = new Date(lastHeartbeat);
    const diffMs = now - heartbeatDate;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}min`;
    if (diffHour < 24) return `${diffHour}h`;
    return `${Math.floor(diffHour / 24)}j`;
  };

  const statusInfo = getStatusInfo();
  const timeSince = getTimeSinceHeartbeat();

  const sizeClasses = {
    sm: { badge: 'px-2 py-0.5 text-xs', dot: 'w-1.5 h-1.5' },
    md: { badge: 'px-2.5 py-0.5 text-xs', dot: 'w-2 h-2' },
    lg: { badge: 'px-3 py-1 text-sm', dot: 'w-2.5 h-2.5' }
  };

  const sc = sizeClasses[size] || sizeClasses.md;

  return (
    <div className="flex items-center gap-2">
      <span className={`inline-flex items-center gap-1.5 rounded-full font-medium border ${statusInfo.bgColor} ${statusInfo.textColor} ${statusInfo.borderColor} ${sc.badge}`}>
        <span className="relative flex">
          <span className={`${sc.dot} ${statusInfo.dotColor} rounded-full`} />
          {statusInfo.pulse && (
            <span className={`absolute ${sc.dot} ${statusInfo.dotColor} rounded-full animate-ping opacity-75`} />
          )}
        </span>
        {statusInfo.label}
      </span>
      {timeSince && (
        <span className="text-xs text-gray-500">{timeSince}</span>
      )}
    </div>
  );
};

export default AgentStatusBadge;
